// Microsoft Clarity utilities for estate planning app
// Provides privacy-safe tracking with automatic data masking

declare global {
  interface Window {
    clarity?: (action: string, ...args: unknown[]) => void;
  }
}

/**
 * Track custom events in Clarity while maintaining privacy
 */
export const trackEvent = (eventName: string, properties?: Record<string, string | number>) => {
  if (typeof window !== 'undefined' && window.clarity) {
    window.clarity('event', eventName, properties);
  }
};

/**
 * Track workflow progress through estate planning steps
 */
export const trackWorkflowStep = (stepName: string, completionStatus: 'started' | 'completed' | 'abandoned') => {
  trackEvent('workflow_step', {
    step: stepName,
    status: completionStatus,
    timestamp: Date.now()
  });
};

/**
 * Track form interactions with privacy protection
 */
export const trackFormInteraction = (formName: string, action: 'focus' | 'blur' | 'submit' | 'error' | 'change') => {
  trackEvent('form_interaction', {
    form: formName,
    action: action,
    timestamp: Date.now()
  });
};

/**
 * Track navigation between frames/pages
 */
export const trackNavigation = (fromPage: string, toPage: string) => {
  trackEvent('navigation', {
    from: fromPage,
    to: toPage,
    timestamp: Date.now()
  });
};

/**
 * CSS classes for automatic data masking in Clarity
 * Add these classes to elements containing sensitive data
 */
export const PRIVACY_CLASSES = {
  // Mask all text content
  SENSITIVE_DATA: 'sensitive-data',
  
  // Mask financial information
  FINANCIAL_AMOUNT: 'financial-amount',
  FINANCIAL_DATA: 'financial-data',
  ACCOUNT_NUMBER: 'account-number',
  
  // Mask personal information
  PERSONAL_INFO: 'personal-info',
  TAX_ID: 'tax-id',
  
  // Mask contact information
  EMAIL_ADDRESS: 'email-address',
  PHONE_NUMBER: 'phone-number',
  
  // Mask addresses
  HOME_ADDRESS: 'home-address'
} as const;

/**
 * Helper function to get privacy class for input types
 */
export const getPrivacyClass = (inputType: 'financial' | 'personal' | 'contact' | 'address' | 'sensitive'): string => {
  switch (inputType) {
    case 'financial':
      return PRIVACY_CLASSES.FINANCIAL_DATA;
    case 'personal':
      return PRIVACY_CLASSES.PERSONAL_INFO;
    case 'contact':
      return PRIVACY_CLASSES.EMAIL_ADDRESS;
    case 'address':
      return PRIVACY_CLASSES.HOME_ADDRESS;
    case 'sensitive':
    default:
      return PRIVACY_CLASSES.SENSITIVE_DATA;
  }
};

/**
 * Set user ID for tracking (use non-PII identifier)
 */
export const setUserIdentity = (userId: string) => {
  if (typeof window !== 'undefined' && window.clarity) {
    // Use hashed or anonymized user ID only
    window.clarity('identify', userId);
  }
};

/**
 * Track estate planning milestones
 */
export const trackMilestone = (milestone: string, data?: Record<string, string | number>) => {
  trackEvent('estate_planning_milestone', {
    milestone,
    ...data,
    timestamp: Date.now()
  });
};